<template>
  <input
    v-show="false"
    :id="iId"
    ref="fileUpload"
    data-sb-regression="sb-file-upload"
    v-bind="$attrs"
    :type="type"
    @change="addFile"
  >
  <div
    v-if="draggable"
    draggable="true"
    class="drag_file__container"
    @drop.prevent="drop"
    @dragenter.prevent="setActive"
    @dragover.prevent="setActive"
    @dragleave.prevent="setInactive"
  >
    <div
      :class="`drag_file__base ${
        (verticalLayout ? 'drag_file__vertical' : 'drag_file__horizontal')
        + (isActive ? ' drag_file__active' : '')
      }`"
    >
      <div class="upload-icon-container">
        <SbLoadingPromiseWrapper
          v-model:loading="loading"
          size="xtsm"
          :loading-promise="loadingPromise"
          :classes="loadingPromiseClasses"
        >
          <img
            :alt="getImgIconAlt"
            :src="getImgIcon"
            class="upload-icon"
            @click="handlePreviewFile"
          >
        </SbLoadingPromiseWrapper>
      </div>
      <div :class="`text-container__base ${verticalLayout ? 'text-container__vertical' : ''}`">
        <sb-text v-if="file">
          {{ getFileName }}
        </sb-text>
        <sb-text v-else>
          {{ $t('sbDraggableFileUpload.dragText') }}
          <a
            href="#"
            @click.prevent="browse"
          >{{ $t('sbDraggableFileUpload.browse') }}</a>
        </sb-text>
        <sb-text v-if="file">
          {{ getFileSize }}
        </sb-text>
        <sb-text v-else>
          {{ getConditionText }}
        </sb-text>
      </div>
      <div
        v-if="removeable && file"
        class="remove-container"
        @click="removeFile"
      >
        <i
          aria-hidden="true"
          class="sb-file-upload-icon
          sb-file-upload-icon--removeable
          sb-file-upload-icon--light
          sb-icon-trash
          delete-icon"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    class="sb-file-upload sb-file-upload__container"
  >
    <label
      :title="fileName"
      :for="iId"
      class="
          sb-file-upload__container
          sb-file-upload__container--center
          sb-file-upload__input
          sb-file-upload-icon-container
          sb-file-upload__container-width
          sb-file-upload--clickable
        "
      :class="{
        'sb-file-upload__container-width--non-removable': !removeable,
        'sb-file-upload--loading': loading,
        [`sb-file-upload--${variation}`]: true,
        [`sb-file-upload-icon-container--${variation}`]: true
      }"
      @click="handleLabelClicked"
    >
      <div class="sb-file-upload__container">
        <SbLoadingPromiseWrapper
          v-model:loading="loading"
          size="xtsm"
          :loading-promise="loadingPromise"
          :classes="loadingPromiseClasses"
        >
          <i
            aria-hidden="true"
            :class="[
              'sb-file-upload',
              'sb-file-upload-icon',
              variation === 'default' ? 'sb-icon-attachment' : 'sb-icon-cloud',
            ]"
          />
        </SbLoadingPromiseWrapper>
      </div>
      <div
        class="sb-file-upload sb-file-upload__label"
        @click="handlePreviewFile"
      >
        {{ inputText }}
      </div>
    </label>
    <div
      v-if="removeable"
      class="
        sb-file-upload
        sb-file-upload__container
        sb-file-upload__container--padded
        sb-file-upload__container--space-left
        sb-file-upload__container--center
        sb-file-upload-icon-container
        sb-file-upload-icon-container--light
        sb-file-upload--clickable
      "
      @click="removeFile"
    >
      <i
        aria-hidden="true"
        class="sb-file-upload-icon sb-file-upload-icon--removeable sb-file-upload-icon--light sb-icon-trash"
      />
    </div>
  </div>
</template>

<script>
import GenericFileEmptyIcon from '@public/media/icons/generic-file-empty.svg';
import GenericFileImportedIcon from '@public/media/icons/generic-file-imported.svg';
import compose from 'lodash/fp/compose';
import uuid from '@src/utils/uuid';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbText from '@src/components/Text/SbText.vue';

/**
 * File Upload is used for capturing a File object.
 */
export default {
  name: 'SbFileUpload',
  components: {
    SbLoadingPromiseWrapper,
    SbText,
  },
  // TODO: this is not working since v3 uprgade
  // model: {
  //   prop: 'file',
  //   event: 'fileChanged',
  // },
  props: {
    /**
     * Value input of the file. To be used with v-model. the corresponding event is `fileChanged`
     */
    file: {
      type: File,
      default: null,
    },
    /**
     * Boolean that determines whether you can drag and drop files into the component.
     */
    draggable: {
      type: Boolean,
      default: false,
    },
    /**
     * Unique if of the file input. Will be generated for you if not provided.
     */
    id: {
      type: String,
      default: null,
    },
    /**
     * Placeholder text for when there is no file selected
     */
    placeholder: {
      type: String,
      default: 'sbFileUpload.chooseFile',
    },
    /**
     * Boolean that determines whether you can clear the file or not.
     */
    removeable: {
      type: Boolean,
      default: true,
    },
    /**
     * Boolean that determines whether you can change the file or not.
     */
    changeable: {
      type: Boolean,
      default: true,
    },
    /**
     * String list of allowed file types. should be in the format `.<ext>`
     */
    allowedFileTypes: {
      type: Array,
      default: () => ([]),
      validator: (vals) => !vals.map((val) => /^\.{1}\w+/i.test(val)).some((val) => val === false),
    },
    /**
     * Boolean determining if the list of allowed file types should be case sensitive.
     */
    caseSensitiveFileTypes: {
      type: Boolean,
      default: true,
    },
    /**
     * To be used to toggle loader if you are fetching the file
     */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
     * Bool that enables download file when clicking on the file name.
     */
    previewFile: {
      type: Boolean,
      default: true,
    },
    /**
     * URL path for the Icon that is shown when the input is empty.
     * If working with MFE make sure that is a full path.
     */
    emptyFileIcon: {
      type: String,
      default: undefined,
    },
    /**
     * URL path for the Icon that is shown when the input is loaded with a file.
     * If working with MFE make sure that is a full path.
     */
    loadedFileIcon: {
      type: String,
      default: undefined,
    },
    /**
     * Max file size in MB to be allowed on the input.
     */
    maxFileSize: {
      type: Number,
      default: undefined,
    },
    /**
     * Make the input display it's component vertically
     */
    verticalLayout: {
      type: Boolean,
      default: false,
    },
    variation: {
      type: String,
      default: 'default',
      validator: (val) => val.match(/(default|button)/),
    },
  },
  emits: [
    /**
     * This event is emitted once the file is selected and a custom previewFile listener is defined and the name is clicked on
     */
    'previewFile',
    /**
     * This event is emitted when a file is added or removed
     */
    'update:file',
    /**
     * This event is emitted when selected file is invalid
     */
    'invalidFile',
  ],
  data: () => ({
    GenericFileEmptyIcon,
    GenericFileImportedIcon,
    type: 'file',
    iId: null,
    loading: false,
    isActive: false,
    inActiveTimeout: null,
  }),
  computed: {
    fileName() {
      return this.file && this.file.name;
    },
    inputText() {
      return this.fileName || this.$t(this.placeholder);
    },
    loadingPromiseClasses() {
      const classes = ['sb-file-upload__loader'];
      if (this.loading) classes.push('sb-file-upload__loader--loading');
      return classes;
    },
    getImgIcon() {
      if (this.file) {
        return this.loadedFileIcon ?? GenericFileImportedIcon;
      }
      return this.emptyFileIcon ?? GenericFileEmptyIcon;
    },
    getImgIconAlt() {
      if (this.file) {
        return this.$t('sbDraggableFileUpload.loadedImageAlt');
      }
      return this.$t('sbDraggableFileUpload.emptyImageAlt');
    },
    getFileName() {
      return this.file?.name ?? undefined;
    },
    getFileSize() {
      const size = this.file?.size;
      if (size === undefined) return undefined;
      const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
      return `${(size / 1024 ** i).toFixed(2) * 1} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
    },
    getConditionText() {
      let text = '';
      let filesText = '';
      this.allowedFileTypes.forEach((type, i) => {
        if (this.allowedFileTypes.length - 1 === i && this.allowedFileTypes.length > 1) {
          filesText += ` ${this.$t('sbDraggableFileUpload.conjuntion')} `;
        }
        filesText += type.replace('.', '').toUpperCase();
        if (this.allowedFileTypes.length - 1 !== i) {
          filesText += `, `;
        }
      });

      text = `${(filesText !== '' ? this.$t('sbDraggableFileUpload.conditions1')
      + filesText : this.$t('sbDraggableFileUpload.anyFile'))
      + this.$t('sbDraggableFileUpload.conditions2')
      }`;

      if (this.maxFileSize) {
        text += `${this.$t('sbDraggableFileUpload.conditions3')}${
          this.maxFileSize ? `${this.maxFileSize} MB` : ''} ${
          this.$t('sbDraggableFileUpload.conditions4')}`;
      }

      return text;
    },
  },
  watch: {
    id: {
      immediate: true,
      handler(newId) {
        this.iId = newId || uuid();
      },
    },
  },
  mounted() {},
  unmounted() {},
  methods: {
    extractFile(e) {
      if (e.dataTransfer?.files
        && e.dataTransfer?.files?.length > 0) {
        return e.dataTransfer.files[0];
      }
      return e.target.files[0];
    },
    validateFile(file) {
      let valid = true;
      try {
        let { allowedFileTypes } = this;
        const fileExtensionRegex = /\.[0-9a-z]+$/i;
        let fileExtension = file && file.name && file.name.match(fileExtensionRegex)[0];

        if (fileExtension && this.allowedFileTypes.length > 0) {
          if (!this.caseSensitiveFileTypes) {
            fileExtension = fileExtension.toLowerCase();
            allowedFileTypes = allowedFileTypes.map((ft) => ft.toLowerCase());
          }
          valid = allowedFileTypes.includes(fileExtension);
        }

        if (fileExtension) {
          valid = this.allowedFileTypes.length > 0 ? this.allowedFileTypes.includes(fileExtension) : true;
        }

        if (this.maxFileSize && valid) {
          valid = (this.maxFileSize * 1024 * 1024) >= file.size;
        }
      } catch (error) {
        valid = false;
      }
      return { valid, file };
    },
    syncFile({ valid, file }) {
      // reset file sets file to null, it will be undefined if the user hits cancel
      if (file === undefined) return;
      if (valid) this.$emit('update:file', file);
      else this.$emit('invalidFile', file);
    },
    handlePreviewFile(e) {
      if (this.previewFile && this.file !== null) {
        if (this.$attrs.previewFile !== undefined) this.$emit('previewFile', this.file);
        else window.open(URL.createObjectURL(this.file));
        e.stopPropagation();
      }
    },
    handleLabelClicked(e) {
      if (this.loading || !this.changeable) e.preventDefault();
    },
    addFile(e) {
      compose(
        this.syncFile,
        this.validateFile,
        this.extractFile,
      )(e);
    },
    removeFile() {
      compose(
        this.syncFile,
        this.resetFile,
      )();
    },
    resetFile() {
      this.$refs.fileUpload.value = null;
      this.type = 'string';
      this.type = 'file';
      return { valid: true, file: null };
    },
    drop(ev) {
      if (this.loading || !this.changeable) ev.preventDefault();
      this.isActive = false;
      this.addFile(ev);
    },
    browse() {
      this.$refs.fileUpload.click();
    },
    setActive() {
      this.isActive = true;
      clearTimeout(this.inActiveTimeout);
    },
    setInactive() {
      this.inActiveTimeout = setTimeout(() => {
        this.isActive = false;
      }, 50);
    },
  },
};
</script>

<style lang='scss'>
@import '@src/components/FileUpload/style/sb-file-upload.scss'
</style>
